import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import React from "react";
import {
  namedOperations,
  PimProductTypeRefInput,
} from "../../../clients/graphqlTypes.js";
import {
  PreferenceListItemFragment,
  usePreferenceListQuery,
  useReorderBrandPreferencesMutation,
  useReorderProductPreferencesMutation,
} from "./BrandPreferences.generated.js";
import { evictRankedProductSearches } from "./useProductPreferences.js";

/** What a preference list and its writes address: one product type, or the global target. */
export type PreferenceTarget =
  | { global: true }
  | { productType: PimProductTypeRefInput };

/**
 * One target's article and brand preferences in search rank order, complete, plus the two
 * reorders. Every preference write refetches it, so the order on screen is PIM's.
 */
export function usePreferenceList(target: PreferenceTarget) {
  const client = useApolloClient();

  const query = usePreferenceListQuery({ client, variables: target });

  // `previousData` keeps the list on screen through the refetch a write triggers. The
  // consumer remounts on a target change, so it never carries another target's items.
  const result = getDataOrNull(
    (query.data ?? query.previousData)?.pimPreferences
  );

  const writeOptions = {
    client,
    refetchQueries: [namedOperations.Query.PreferenceList],
    awaitRefetchQueries: true,
    update: evictRankedProductSearches,
  };

  const [reorderProductPreferences, reorderProductsState] =
    useReorderProductPreferencesMutation(writeOptions);
  const [reorderBrandPreferences, reorderBrandsState] =
    useReorderBrandPreferencesMutation(writeOptions);

  const loading = query.loading && result === null;

  return React.useMemo(
    () => ({
      items: result?.items ?? EMPTY_ITEMS,
      loading,
      error: query.error ?? null,
      refetch: query.refetch,
      reorderProductPreferences,
      reorderBrandPreferences,
      reordering: reorderProductsState.loading || reorderBrandsState.loading,
    }),
    [
      result,
      loading,
      query.error,
      query.refetch,
      reorderProductPreferences,
      reorderBrandPreferences,
      reorderProductsState.loading,
      reorderBrandsState.loading,
    ]
  );
}

const EMPTY_ITEMS: readonly PreferenceListItemFragment[] = [];

export type UsePreferenceList = ReturnType<typeof usePreferenceList>;
